import axios from '../axios';

// ---- Types ----

export interface IAuditTrailChange {
  fieldName: string;
  fieldLabel?: string;
  oldValue?: string;
  oldLabel?: string;
  newValue?: string;
  newLabel?: string;
  action?: 'ADD' | 'EDIT' | 'DELETE';
  actionLabel?: string;
}

export interface IAuditTrail {
  id: string;
  created: string;
  action?: string;
  actionLabel?: string;
  kind?: string;
  targetId?: string;
  targetName?: string;
  user?: {
    id: string;
    name: string;
    email?: string;
  };
  changes?: IAuditTrailChange[];
}

export type LeadActivityType = 'CREATED' | 'STATUS_CHANGE' | 'NOTE' | 'EMAIL_SENT' | 'CONVERTED' | 'UPDATE' | 'SYSTEM';

export interface ILeadActivityItem {
  id: string;
  type: LeadActivityType;
  timestamp: string;
  authorName?: string;
  authorEmail?: string;
  title: string;
  description?: string;
  serviceName?: string;
  oldValue?: string;
  oldLabel?: string;
  newValue?: string;
  newLabel?: string;
  noteId?: string;
  rawNote?: ILeadNote;
  rawAuditTrail?: IAuditTrail;
}

export interface ILead {
  id: string;
  created: string;
  updated: string;
  placeId?: string;
  placeName?: string;
  businessId?: string;
  ytjName?: string;
  companyForm?: string;
  businessLine?: string;
  street?: string;
  postCode?: string;
  city?: string;
  phone?: string;
  email?: string;
  website?: string;
  contactPerson?: string;
  status: LeadStatus;
  contactMethod?: string;
  generatedEmailSubject?: string;
  generatedEmailBody?: string;
  emailSentAt?: string;
  operatorId?: string;
  isToiminimi?: boolean;
  archived?: boolean;
  source?: 'MANUAL' | 'OUTREACH_BATCH' | 'IMPORT';
  batchId?: string;
  description?: string;
  notes?: ILeadNote[];
  auditTrails?: IAuditTrail[];
  convertedToProvider?: boolean;
  convertedToProviderDate?: string;
  serviceToken?: string;
  linkedOrgId?: string;
}

export interface ILeadNote {
  id: string;
  created: string;
  updated: string;
  leadId?: string;
  organizationId?: string;
  userId: string;
  userName: string;
  content: string;
  serviceToken?: string;
  serviceName?: string;
}

export enum LeadStatus {
  NOT_CONTACTED = 'NOT_CONTACTED',
  CONTACTED = 'CONTACTED',
  NOT_INTERESTED = 'NOT_INTERESTED',
  CONTRACT = 'CONTRACT',
}

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  [LeadStatus.NOT_CONTACTED]: 'Ei kontaktoitu',
  [LeadStatus.CONTACTED]: 'Kontaktoitu',
  [LeadStatus.NOT_INTERESTED]: 'Ei kiinnosta',
  [LeadStatus.CONTRACT]: 'Sopimus',
};

export interface ISaveLead {
  placeId?: string;
  placeName?: string;
  businessId?: string;
  ytjName?: string;
  companyForm?: string;
  businessLine?: string;
  street?: string;
  postCode?: string;
  city?: string;
  phone?: string;
  email?: string;
  website?: string;
  contactPerson?: string;
  status?: string;
  contactMethod?: string;
  generatedEmailSubject?: string;
  generatedEmailBody?: string;
  isToiminimi?: boolean;
  description?: string;
}

export interface IDuplicateCheckResult {
  isDuplicate: boolean;
  existingLead?: ILead;
}

export interface ISendEmailResult {
  success: boolean;
  sentTo: string;
  isDevelopment: boolean;
}

export interface IBulkConvertResult {
  created: number;
  skipped: number;
  alreadyConverted: number;
  errors: number;
  total: number;
  details: Array<{
    name?: string;
    leadId?: string;
    status: 'CREATED' | 'SKIPPED' | 'ALREADY_CONVERTED' | 'ERROR';
    reason?: string;
    organizationId?: string;
  }>;
}

// ---- API functions ----

export const listLeads = async (): Promise<ILead[]> => {
  const response = await axios.get('/json/leads');
  return response.data || [];
};

export const getLead = async (id: string): Promise<ILead> => {
  const response = await axios.get(`/json/leads/${id}`);
  return response.data;
};

export const saveLead = async (data: ISaveLead): Promise<ILead> => {
  const response = await axios.post('/json/leads', { action: 'save', ...data });
  return response.data;
};

export const updateLead = async (id: string, data: Partial<ILead>): Promise<ILead> => {
  const response = await axios.post('/json/leads', { action: 'update', id, ...data });
  return response.data;
};

export const checkDuplicate = async (placeId?: string, businessId?: string): Promise<IDuplicateCheckResult> => {
  const response = await axios.post('/json/leads', { action: 'check-duplicate', placeId, businessId });
  return response.data;
};

export const sendEmail = async (id: string): Promise<ISendEmailResult> => {
  const response = await axios.post('/json/leads', { action: 'send-email', id });
  return response.data;
};

export const deleteLead = async (id: string): Promise<boolean> => {
  const response = await axios.post('/json/leads', { action: 'delete', id });
  return response.data;
};

// ---- Notes ----

export const listLeadNotes = async (leadId: string): Promise<ILeadNote[]> => {
  const response = await axios.get(`/json/lead-notes?leadId=${leadId}`);
  return response.data || [];
};

export const addLeadNote = async (
  leadId: string,
  content: string,
  serviceToken?: string,
  serviceName?: string
): Promise<ILeadNote> => {
  const response = await axios.post('/json/lead-notes', {
    leadId,
    content,
    serviceToken,
    serviceName,
  });
  return response.data;
};

export const listOrganizationNotes = async (organizationId: string): Promise<ILeadNote[]> => {
  const response = await axios.get(`/json/lead-notes?organizationId=${organizationId}`);
  return response.data || [];
};

export const addOrganizationNote = async (
  organizationId: string,
  content: string,
  serviceToken?: string,
  serviceName?: string
): Promise<ILeadNote> => {
  const response = await axios.post('/json/lead-notes', {
    organizationId,
    content,
    serviceToken,
    serviceName,
  });
  return response.data;
};

export const updateLeadNote = async (
  noteId: string,
  content: string,
  serviceToken?: string,
  serviceName?: string
): Promise<ILeadNote> => {
  const response = await axios.put(`/json/lead-notes/${noteId}`, {
    content,
    serviceToken,
    serviceName,
  });
  return response.data;
};

export const deleteLeadNote = async (noteId: string): Promise<boolean> => {
  const response = await axios.delete(`/json/lead-notes/${noteId}`);
  return response.data;
};

export const bulkConvertToProviders = async (serviceToken: string, leadIds: string[]): Promise<IBulkConvertResult> => {
  const response = await axios.post('/json/leads', {
    action: 'bulk-convert-to-providers',
    serviceToken,
    leadIds,
  });
  return response.data;
};

export function buildLeadTimeline(lead: ILead): ILeadActivityItem[] {
  const items: ILeadActivityItem[] = [];

  // 1. Audit trail events
  let hasCreatedAudit = false;
  if (lead.auditTrails && lead.auditTrails.length > 0) {
    for (const trail of lead.auditTrails) {
      const author = trail.user?.name || trail.user?.email || 'Järjestelmä';
      const ts = trail.created;

      if (trail.action === 'SAVE_NEW') {
        hasCreatedAudit = true;
        items.push({
          id: `audit-${trail.id || Math.random()}`,
          type: 'CREATED',
          timestamp: ts,
          authorName: author,
          authorEmail: trail.user?.email,
          title: 'Liidi luotu',
          description: lead.source ? `Lähde: ${lead.source === 'OUTREACH_BATCH' ? 'Taustahaku' : lead.source === 'MANUAL' ? 'Manuaalinen lisäys' : lead.source}` : 'Luotu järjestelmään',
          rawAuditTrail: trail,
        });
      } else if (trail.changes && trail.changes.length > 0) {
        for (const change of trail.changes) {
          if (change.fieldName === 'status') {
            items.push({
              id: `audit-${trail.id}-${change.fieldName}`,
              type: 'STATUS_CHANGE',
              timestamp: ts,
              authorName: author,
              authorEmail: trail.user?.email,
              title: 'Tilan muutos',
              oldValue: change.oldValue,
              oldLabel: change.oldLabel || change.oldValue,
              newValue: change.newValue,
              newLabel: change.newLabel || change.newValue,
              description: `${change.oldLabel || change.oldValue || 'Ei tilaa'} ➔ ${change.newLabel || change.newValue}`,
              rawAuditTrail: trail,
            });
          } else if (change.fieldName === 'emailSentAt') {
            items.push({
              id: `audit-${trail.id}-email`,
              type: 'EMAIL_SENT',
              timestamp: ts,
              authorName: author,
              authorEmail: trail.user?.email,
              title: 'Sähköposti lähetetty',
              description: change.newLabel || change.newValue || 'Outreach-sähköposti lähetetty',
              rawAuditTrail: trail,
            });
          } else if (change.fieldName === 'convertedToProvider') {
            items.push({
              id: `audit-${trail.id}-convert`,
              type: 'CONVERTED',
              timestamp: ts,
              authorName: author,
              authorEmail: trail.user?.email,
              title: 'Konvertoitu palveluntarjoajaksi',
              description: change.newLabel || 'Luotu palveluntarjoajaksi HankiX-järjestelmään',
              rawAuditTrail: trail,
            });
          } else {
            items.push({
              id: `audit-${trail.id}-${change.fieldName}`,
              type: 'UPDATE',
              timestamp: ts,
              authorName: author,
              authorEmail: trail.user?.email,
              title: `${change.fieldLabel || change.fieldName} päivitetty`,
              oldValue: change.oldValue,
              newValue: change.newValue,
              description: change.newLabel || `${change.oldValue || '-'} ➔ ${change.newValue || '-'}`,
              rawAuditTrail: trail,
            });
          }
        }
      } else {
        items.push({
          id: `audit-${trail.id}`,
          type: 'SYSTEM',
          timestamp: ts,
          authorName: author,
          authorEmail: trail.user?.email,
          title: trail.actionLabel || trail.action || 'Tapahtuma',
          rawAuditTrail: trail,
        });
      }
    }
  }

  // Fallback: If no created audit trail, fallback to lead.created
  if (!hasCreatedAudit && lead.created) {
    items.push({
      id: `lead-created-${lead.id}`,
      type: 'CREATED',
      timestamp: lead.created,
      title: 'Liidi luotu',
      description: lead.source ? `Lähde: ${lead.source === 'OUTREACH_BATCH' ? 'Taustahaku' : lead.source === 'MANUAL' ? 'Manuaalinen lisäys' : lead.source}` : 'Luotu järjestelmään',
    });
  }

  // Fallback: If emailSentAt exists but not represented in audit trail
  if (lead.emailSentAt && !items.some(i => i.type === 'EMAIL_SENT')) {
    items.push({
      id: `lead-email-${lead.id}`,
      type: 'EMAIL_SENT',
      timestamp: lead.emailSentAt,
      title: 'Sähköposti lähetetty',
      description: `Lähetetty osoitteeseen: ${lead.email || '-'}`,
    });
  }

  // 2. Add notes
  if (lead.notes && lead.notes.length > 0) {
    for (const note of lead.notes) {
      items.push({
        id: `note-${note.id}`,
        type: 'NOTE',
        timestamp: note.created,
        authorName: note.userName || 'Käyttäjä',
        title: 'Muistiinpano',
        description: note.content,
        serviceName: note.serviceName,
        noteId: note.id,
        rawNote: note,
      });
    }
  }

  // Sort chronologically descending (newest first)
  items.sort((a, b) => {
    const tA = new Date(a.timestamp || 0).getTime();
    const tB = new Date(b.timestamp || 0).getTime();
    return tB - tA;
  });

  return items;
}

export const listAuditTrailsForTarget = async (targetId: string): Promise<IAuditTrail[]> => {
  if (!targetId) return [];
  try {
    const numId = parseInt(targetId, 10);
    const response = await axios.post(`/json/audittrails/list?targetId=${numId}`, {
      targetId: numId,
    });
    const data = response.data;
    if (!data || !Array.isArray(data)) {
      return [];
    }
    // Strict client-side filter to guarantee only target-related audit records are displayed
    return data.filter((trail: IAuditTrail) => {
      if (!trail) return false;
      const tId = trail.targetId != null ? String(trail.targetId) : '';
      const pId = (trail as any).parentId != null ? String((trail as any).parentId) : '';
      const strId = String(targetId);
      const strNum = String(numId);
      return tId === strId || tId === strNum || pId === strId || pId === strNum;
    });
  } catch (err) {
    console.error('Failed to load audit trails for target', targetId, err);
    return [];
  }
};

export function buildOrganizationTimeline(params: {
  notes?: ILeadNote[];
  auditTrails?: IAuditTrail[];
  created?: string;
  organizationName?: string;
}): ILeadActivityItem[] {
  const { notes, auditTrails, created } = params;
  const items: ILeadActivityItem[] = [];

  let hasCreatedAudit = false;
  if (auditTrails && auditTrails.length > 0) {
    for (const trail of auditTrails) {
      const author = trail.user?.name || trail.user?.email || 'Järjestelmä';
      const ts = trail.created;

      if (trail.action === 'SAVE_NEW') {
        hasCreatedAudit = true;
        const leadConvertChange = trail.changes?.find(c => c.fieldName === 'source' || c.newValue === 'LEAD_CONVERSION');
        items.push({
          id: `audit-${trail.id || Math.random()}`,
          type: leadConvertChange ? 'CONVERTED' : 'CREATED',
          timestamp: ts,
          authorName: author,
          authorEmail: trail.user?.email,
          title: leadConvertChange ? 'Konvertoitu liidistä' : 'Palveluntarjoaja luotu',
          description: leadConvertChange ? (leadConvertChange.newLabel || leadConvertChange.newValue) : 'Luotu järjestelmään',
          rawAuditTrail: trail,
        });
      } else if (trail.changes && trail.changes.length > 0) {
        for (const change of trail.changes) {
          items.push({
            id: `audit-${trail.id}-${change.fieldName}`,
            type: 'UPDATE',
            timestamp: ts,
            authorName: author,
            authorEmail: trail.user?.email,
            title: `${change.fieldLabel || change.fieldName} päivitetty`,
            oldValue: change.oldValue,
            newValue: change.newValue,
            description: `${change.fieldLabel || change.fieldName}: ${change.oldValue || '-'} ➔ ${change.newValue || '-'}`,
            rawAuditTrail: trail,
          });
        }
      } else {
        items.push({
          id: `audit-${trail.id}`,
          type: 'SYSTEM',
          timestamp: ts,
          authorName: author,
          authorEmail: trail.user?.email,
          title: trail.actionLabel || trail.action || 'Tapahtuma',
          rawAuditTrail: trail,
        });
      }
    }
  }

  // Fallback: If no created audit trail, fallback to created param
  if (!hasCreatedAudit && created) {
    items.push({
      id: `org-created-${created}`,
      type: 'CREATED',
      timestamp: created,
      title: 'Palveluntarjoaja luotu',
      description: 'Luotu järjestelmään',
    });
  }

  // Notes
  if (notes && notes.length > 0) {
    for (const note of notes) {
      items.push({
        id: `note-${note.id}`,
        type: 'NOTE',
        timestamp: note.created,
        authorName: note.userName || 'Käyttäjä',
        title: 'Muistiinpano',
        description: note.content,
        serviceName: note.serviceName,
        noteId: note.id,
        rawNote: note,
      });
    }
  }

  // Sort descending
  items.sort((a, b) => {
    const tA = new Date(a.timestamp || 0).getTime();
    const tB = new Date(b.timestamp || 0).getTime();
    return tB - tA;
  });

  return items;
}

const LeadService = {
  listLeads,
  getLead,
  saveLead,
  updateLead,
  checkDuplicate,
  sendEmail,
  deleteLead,
  listLeadNotes,
  addLeadNote,
  listOrganizationNotes,
  addOrganizationNote,
  updateLeadNote,
  deleteLeadNote,
  bulkConvertToProviders,
  buildLeadTimeline,
  listAuditTrailsForTarget,
  buildOrganizationTimeline,
};

export default LeadService;

